import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminCustomers } from '@/api/hooks/useAdminCustomers';
import { formatCurrency } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { ChevronLeft, ChevronRight, Users } from 'lucide-react';
import CustomerDetailModal from '@/components/admin/CustomerDetailModal';

const FLAG_COLORS: Record<string, string> = {
  BANNED: 'bg-red-500 text-white',
  NO_SHOW_RISK: 'bg-orange-500 text-white',
  REQUIRES_DEPOSIT: 'bg-amber-500 text-white',
  VIP: 'bg-yellow-400 text-black',
  WATCH: 'bg-blue-500 text-white',
};

const FLAG_TYPES = ['VIP', 'BANNED', 'NO_SHOW_RISK', 'REQUIRES_DEPOSIT', 'WATCH'];

export default function CustomersPage() {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [flagFilter, setFlagFilter] = useState('');
  const [page, setPage] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = useAdminCustomers({
    search: debouncedSearch || undefined,
    flagType: flagFilter || undefined,
    page,
    size: 20,
  });

  const customers = data?.customers ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t('admin.customers')}</h1>

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          <input
            type="text"
            placeholder={t('admin.search_customer_placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-64 rounded-lg border border-input bg-transparent px-2.5 text-sm outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
          />
          <select
            value={flagFilter}
            onChange={(e) => { setFlagFilter(e.target.value); setPage(0); }}
            className="h-8 rounded-lg border border-input bg-transparent px-2 text-sm outline-none"
          >
            <option value="">{t('admin.all_customers')}</option>
            {FLAG_TYPES.map((ft) => (
              <option key={ft} value={ft}>{ft}</option>
            ))}
          </select>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
            </div>
          ) : customers.length === 0 ? (
            <div className="py-16 text-center">
              <Users className="h-12 w-12 mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">{t('admin.no_customers')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[180px]">{t('admin.col_customer')}</TableHead>
                    <TableHead className="min-w-[120px]">{t('admin.col_flags')}</TableHead>
                    <TableHead className="text-right min-w-[80px]">{t('admin.total_bookings')}</TableHead>
                    <TableHead className="text-right min-w-[80px]">{t('admin.col_no_shows')}</TableHead>
                    <TableHead className="text-right min-w-[100px]">{t('admin.col_total_spent')}</TableHead>
                    <TableHead className="hidden md:table-cell min-w-[100px]">{t('admin.col_last_booking')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.map((c) => (
                    <TableRow
                      key={c.userId}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setSelectedUserId(c.userId)}
                    >
                      <TableCell>
                        <div className="text-sm font-medium">{c.firstName} {c.lastName}</div>
                        <div className="text-xs text-muted-foreground">{c.email}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {c.activeFlags.map((f) => (
                            <Badge key={f.id} className={`text-[10px] ${FLAG_COLORS[f.flagType] ?? ''}`}>
                              {f.flagType}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">{c.totalBookings}</TableCell>
                      <TableCell className={`text-right font-mono text-sm ${c.noShowCount > 0 ? 'text-red-600' : ''}`}>
                        {c.noShowCount}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCurrency(c.totalSpent, 'CAD', i18n.language)}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                        {c.lastBookingDate ? new Date(c.lastBookingDate).toLocaleDateString() : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {t('admin.page_of', { page: pagination.page + 1, total: pagination.totalPages })}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={pagination.page === 0} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="mr-1 h-4 w-4" /> {t('admin.previous')}
            </Button>
            <Button variant="outline" size="sm" disabled={pagination.page >= pagination.totalPages - 1} onClick={() => setPage((p) => p + 1)}>
              {t('admin.next')} <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <CustomerDetailModal
        userId={selectedUserId}
        open={!!selectedUserId}
        onOpenChange={(open) => { if (!open) setSelectedUserId(null); }}
      />
    </div>
  );
}
